import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DeleteIcon, LogOutIcon } from "lucide-react";
import { useProjects } from "@/context/ProjectContext";
import { GetProjectResponseDto } from "@/dto/get-project-response.dto";
import { useAuth } from "@/context/AuthContext";

interface RemoveMemberDialogProps {
  project: GetProjectResponseDto;
  member:
    | GetProjectResponseDto["members"][number]
    | GetProjectResponseDto["createdBy"];
}

export function RemoveMemberDialog({
  project,
  member,
}: RemoveMemberDialogProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { removeMemberFromProject, removingUser } = useProjects();

  const handleConfirmDelete = () => {
    removeMemberFromProject(project._id, member._id);

    if (leave) {
      navigate("/projects");
    }
  };

  const leave = user._id === member._id;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="destructive"
          size="sm"
          className="w-full flex items-center justify-between"
        >
          {leave ? (
            <>
              Leave <LogOutIcon className="h-4 w-4" />
            </>
          ) : (
            <>
              Remove <DeleteIcon className="h-4 w-4" />
            </>
          )}
        </Button>
      </DialogTrigger>

      <DialogPortal>
        <DialogOverlay className="bg-black/45" />
        <DialogContent>
          <DialogTitle className="text-lg font-semibold">
            {leave ? "Confirm Leaving" : "Confirm Deletion"}
          </DialogTitle>
          <DialogDescription>
            {leave ? (
              <>
                Are you sure you want to <strong>leave</strong> the project{" "}
                <strong>{project.name}</strong>? You will lose access to this
                project. This action cannot be undone.
              </>
            ) : (
              <>
                Are you sure you want to remove{" "}
                <strong>{member.fullName}</strong> from the project{" "}
                <strong>{project.name}</strong>? This action cannot be undone.
              </>
            )}
          </DialogDescription>
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
            }}
          >
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <DialogClose asChild>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={removingUser}
              >
                {removingUser
                  ? leave
                    ? "Leaving..."
                    : "Removing..."
                  : "Confirm"}
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
